import React from 'react';
import { motion, type HTMLMotionProps } from 'motion/react';

/**
 * PressableCard — A reusable wrapper that provides tactile press feedback.
 *
 * Press in:  scale → 0.97, opacity → 0.85  (~100ms fast spring)
 * Press out: spring back to 1.0 / 1.0       (~150ms with slight overshoot)
 *
 * Use this around any tappable card, list row, or surface that navigates
 * on click. Do NOT use on text inputs, toggles, or already-animated buttons.
 */

export interface PressableCardProps extends HTMLMotionProps<'div'> {
  /** Disable the press animation (e.g. while loading) */
  disabled?: boolean;
}

export const PressableCard = React.forwardRef<HTMLDivElement, PressableCardProps>(
  ({ children, disabled, style, ...rest }, ref) => {
    return (
      <motion.div
        ref={ref}
        whileTap={
          disabled
            ? undefined
            : { scale: 0.97, opacity: 0.85 }
        }
        transition={{
          type: 'spring',
          damping: 20,
          stiffness: 300,
          mass: 0.6,
        }}
        style={{ cursor: disabled ? undefined : 'pointer', ...style }}
        {...rest}
      >
        {children}
      </motion.div>
    );
  }
);

PressableCard.displayName = 'PressableCard';
